import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { BiX } from 'react-icons/bi';
import { CalendarEventChip } from '@/pages/calendar/CalendarEventChip';
import { formatDayLong } from '@/lib/format';
import type { CalendarEvent } from '@/types/shared';
import './DayDetailPopover.css';

interface DayDetailPopoverProps {
  id: string;
  headingId: string;
  dateStr: string;
  events: CalendarEvent[];
  language: string;
  onClose: () => void;
}

/**
 * T104 §2.7 day-detail popover ("+N more" disclosure). Non-modal (`aria-modal="false"`) — this is
 * inspectable content, not a blocking decision, so it deliberately does NOT reuse the
 * focus-trapping `ConfirmDialog` pattern. Focus moves to the heading on open; `Esc` or an
 * outside-click closes it (the caller's `onClose` is responsible for returning focus to the
 * "+N more" trigger, matching `useWidgetRetryFocus`'s "always return focus somewhere predictable"
 * discipline).
 */
export function DayDetailPopover({
  id,
  headingId,
  dateStr,
  events,
  language,
  onClose,
}: DayDetailPopoverProps) {
  const { t } = useTranslation();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    }

    function onPointerDown(e: MouseEvent) {
      const target = e.target as HTMLElement | null;
      // The "+N more" button that opened this popover owns its own open/close toggle on `click`
      // — ignore its own mousedown here so a re-click doesn't close-then-reopen it.
      if (target?.closest('[data-cal-overflow-trigger]')) return;
      if (popoverRef.current && !popoverRef.current.contains(target)) {
        onClose();
      }
    }

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [onClose]);

  return (
    <div
      id={id}
      ref={popoverRef}
      role="dialog"
      aria-modal="false"
      aria-labelledby={headingId}
      className="fa-cal-popover"
    >
      <div className="fa-cal-popover__header">
        <h2 id={headingId} ref={headingRef} tabIndex={-1} className="fa-cal-popover__heading">
          {formatDayLong(dateStr, language)}
        </h2>
        <button
          type="button"
          className="fa-cal-popover__close"
          onClick={onClose}
          aria-label={t('calendar.overflow.dayPopoverCloseLabel')}
        >
          <BiX aria-hidden="true" />
        </button>
      </div>
      <ul className="fa-cal-popover__list">
        {events.map((event) => (
          <li key={event.id} className="fa-cal-popover__item">
            <CalendarEventChip event={event} language={language} variant="expanded" />
          </li>
        ))}
      </ul>
    </div>
  );
}
